import type { JobListParams } from './list-params.js'

type JobStatus = JobListParams['status'][number]

/**
 * The Jobs list as a worklist: three tabs, and inside each, a status derived
 * from dates rather than set by hand.
 *
 * One pipeline — Quote, then Work Order, then Completed — so a job is in
 * exactly one tab, decided by `Job.status`. Inside a tab the sub-status answers
 * "what needs to happen next", and it is computed from the roll-up columns the
 * `fieldops_refresh_job_worklist` trigger maintains on `Job`. Nothing here is a
 * field anyone edits: fill in a date and the job moves itself.
 *
 * This module is the single declaration of that scheme, and it lives in
 * `shared` because two very different consumers have to agree about it:
 *
 * - the client, which derives a row's status in TypeScript to draw it, and
 * - the API, which turns the same rule into a `where` clause to count and
 *   filter across all 68,000 jobs rather than the fifty on screen.
 *
 * Each entry below therefore carries both forms, adjacent, so a change to one
 * is a visibly incomplete change to the other. `apps/api/test/worklist.test.ts`
 * closes the loop against a real database: for every status it asserts that
 * `worklistWhere` selects exactly the rows `deriveWorklistStatus` labels.
 *
 * Why the tab comes from `status` and the sub-status from dates: the dates are
 * the things that move, and one of the transitions has no write behind it at
 * all. `Scheduled` becomes `Follow-up` because the clock passed the booking,
 * so any stored answer would be stale. See decision 35.
 */

/** Badge tones, mirroring the client's `Tone` union without importing it. */
export type WorklistTone = 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'purple'

export const JOB_TABS = [
  {
    key: 'QUOTE',
    label: 'Quote',
    caption: 'Not a work order yet',
    statuses: ['LEAD', 'QUOTING', 'QUOTED', 'CANCELLED'],
  },
  {
    key: 'WORK_ORDER',
    label: 'Work Order',
    caption: 'Accepted, in progress',
    statuses: ['APPROVED', 'SCHEDULED', 'IN_PROGRESS', 'ON_HOLD', 'AWAITING_PARTS'],
  },
  {
    key: 'COMPLETED',
    label: 'Completed',
    caption: 'Work done, get paid',
    statuses: ['COMPLETED'],
  },
] as const satisfies ReadonlyArray<{
  key: string
  label: string
  caption: string
  statuses: readonly JobStatus[]
}>

export type JobTabKey = (typeof JOB_TABS)[number]['key']

/**
 * `CANCELLED` sits in the Quote tab as Closed / lost, because that is where a
 * lost job came from and where anyone would look for it. It replaces the
 * separate Unsuccessful stage the four-stage filter used to offer.
 *
 * The mapping has to stay total over all ten `JobStatus` values, for the same
 * reason it did as stages: a status that escaped it would be a job that appears
 * in no tab and is therefore invisible. `job-worklist.test.ts` asserts it.
 */
export const JOB_TAB_KEYS = JOB_TABS.map((tab) => tab.key) as readonly JobTabKey[]

/** The columns the derivation reads, all maintained by the database. */
export interface WorklistInput {
  status: string
  quoteSentAt: Date | string | null
  quotePriced: boolean
  lastVisitAt: Date | string | null
  invoiceSentAt: Date | string | null
  invoicePaid: boolean
}

/**
 * A `where` fragment over `Job`, structurally compatible with Prisma's
 * `JobWhereInput` but declared here so `shared` need not depend on the client.
 */
export interface WorklistWhere {
  status?: { in: JobStatus[] }
  quoteSentAt?: null | { not: null }
  quotePriced?: boolean
  lastVisitAt?: null | { gt: Date } | { lte: Date }
  invoiceSentAt?: null | { not: null }
  invoicePaid?: boolean
}

/** The Quote tab minus `CANCELLED`, which is Closed / lost rather than open. */
const QUOTE_OPEN = ['LEAD', 'QUOTING', 'QUOTED'] satisfies JobStatus[]
const WORK_ORDER = [...JOB_TABS[1].statuses] satisfies JobStatus[]

interface WorklistDefinition {
  key: string
  tab: JobTabKey
  label: string
  /** What the status means, shown on the group header. */
  meaning: string
  /** What to do about it. Null means this status needs nothing. */
  action: string | null
  tone: WorklistTone
  /** True when a job is in this status. Evaluated in tab order. */
  matches: (job: WorklistInput, now: Date) => boolean
  /** The same rule as a query, for counting and filtering server-side. */
  where: (now: Date) => WorklistWhere
}

const has = (value: Date | string | null): boolean => value !== null && value !== undefined
const at = (value: Date | string): Date => (value instanceof Date ? value : new Date(value))

/**
 * Ordered as the job moves through them, which is the order the office names
 * them in: Draft, Priced, Sent; Unscheduled, Booked, Follow-up. Within a tab
 * the predicates are mutually exclusive and exhaustive, so the first match is
 * the answer.
 *
 * Keys outlive labels because they are in saved links: `AWAITING_REPLY` reads
 * Sent and `SCHEDULED` reads Booked.
 */
export const WORKLIST_STATUSES = [
  {
    key: 'DRAFT',
    tab: 'QUOTE',
    label: 'Draft',
    meaning: 'No priced lines on the quote yet',
    action: 'Price the quote',
    tone: 'neutral',
    matches: (job) => job.status !== 'CANCELLED' && !has(job.quoteSentAt) && !job.quotePriced,
    where: () => ({ status: { in: QUOTE_OPEN }, quoteSentAt: null, quotePriced: false }),
  },
  {
    key: 'PRICED',
    tab: 'QUOTE',
    label: 'Priced',
    meaning: 'The quote has a value, not sent to the customer yet',
    action: 'Send the quote',
    tone: 'blue',
    matches: (job) => job.status !== 'CANCELLED' && !has(job.quoteSentAt) && job.quotePriced,
    where: () => ({ status: { in: QUOTE_OPEN }, quoteSentAt: null, quotePriced: true }),
  },
  {
    key: 'AWAITING_REPLY',
    tab: 'QUOTE',
    label: 'Sent',
    meaning: 'Quote is out, no answer yet',
    action: 'Call and follow up',
    tone: 'amber',
    matches: (job) => job.status !== 'CANCELLED' && has(job.quoteSentAt),
    where: () => ({ status: { in: QUOTE_OPEN }, quoteSentAt: { not: null } }),
  },
  {
    key: 'CLOSED_LOST',
    tab: 'QUOTE',
    label: 'Closed / lost',
    meaning: 'Declined or not going ahead',
    action: null,
    tone: 'neutral',
    matches: (job) => job.status === 'CANCELLED',
    where: () => ({ status: { in: ['CANCELLED'] } }),
  },
  {
    key: 'UNSCHEDULED',
    tab: 'WORK_ORDER',
    label: 'Unscheduled',
    meaning: 'A work order with nothing booked',
    action: 'Book it in',
    tone: 'amber',
    // No visits at all: the maximum over no rows is null.
    matches: (job) => !has(job.lastVisitAt),
    where: () => ({ status: { in: WORK_ORDER }, lastVisitAt: null }),
  },
  {
    key: 'SCHEDULED',
    tab: 'WORK_ORDER',
    label: 'Booked',
    meaning: 'A booking is coming up',
    action: null,
    tone: 'blue',
    matches: (job, now) => has(job.lastVisitAt) && at(job.lastVisitAt!) > now,
    where: (now) => ({ status: { in: WORK_ORDER }, lastVisitAt: { gt: now } }),
  },
  {
    key: 'FOLLOW_UP',
    tab: 'WORK_ORDER',
    label: 'Follow-up',
    meaning: 'Been on site, not marked complete, nothing booked next',
    action: 'Rebook or complete the job',
    tone: 'red',
    // The latest booking is in the past, so there is no future one either.
    matches: (job, now) => has(job.lastVisitAt) && at(job.lastVisitAt!) <= now,
    where: (now) => ({ status: { in: WORK_ORDER }, lastVisitAt: { lte: now } }),
  },
  {
    key: 'INVOICE_PENDING',
    tab: 'COMPLETED',
    label: 'Invoice pending',
    meaning: 'Work is done, no invoice raised',
    action: 'Raise the invoice',
    tone: 'purple',
    matches: (job) => !job.invoicePaid && !has(job.invoiceSentAt),
    where: () => ({ status: { in: ['COMPLETED'] }, invoicePaid: false, invoiceSentAt: null }),
  },
  {
    key: 'AWAITING_PAYMENT',
    tab: 'COMPLETED',
    label: 'Awaiting payment',
    meaning: 'Invoice sent, payment not in',
    action: 'Chase payment',
    tone: 'amber',
    matches: (job) => !job.invoicePaid && has(job.invoiceSentAt),
    where: () => ({ status: { in: ['COMPLETED'] }, invoicePaid: false, invoiceSentAt: { not: null } }),
  },
  {
    key: 'PAID',
    tab: 'COMPLETED',
    label: 'Paid / closed',
    meaning: 'Sent, paid, job closed out',
    action: null,
    tone: 'green',
    // Detected from the invoice's status, because there is no payment date to
    // read: `Payment` is empty and `amountPaid` is zero on every invoice the
    // ServiceM8 migration marked paid. Becomes a date when payments load.
    matches: (job) => job.invoicePaid,
    where: () => ({ status: { in: ['COMPLETED'] }, invoicePaid: true }),
  },
] as const satisfies ReadonlyArray<WorklistDefinition>

export type WorklistStatusKey = (typeof WORKLIST_STATUSES)[number]['key']

export const WORKLIST_STATUS_KEYS = WORKLIST_STATUSES.map(
  (entry) => entry.key,
) as readonly WorklistStatusKey[]

export function tabOf(status: string): JobTabKey | undefined {
  return JOB_TABS.find((tab) => (tab.statuses as readonly string[]).includes(status))?.key
}

export function tabLabel(key: JobTabKey): string {
  return JOB_TABS.find((tab) => tab.key === key)?.label ?? key
}

/** The statuses a tab shows, in the order the tab presents them. */
export function statusesInTab(tab: JobTabKey): WorklistStatusKey[] {
  return WORKLIST_STATUSES.filter((entry) => entry.tab === tab).map((entry) => entry.key)
}

export function worklistStatus(key: WorklistStatusKey): (typeof WORKLIST_STATUSES)[number] {
  const found = WORKLIST_STATUSES.find((entry) => entry.key === key)
  if (!found) throw new Error(`Unknown worklist status: ${key}`)
  return found
}

/** Every `JobStatus` that could produce this worklist status. */
export function jobStatusesFor(key: WorklistStatusKey): JobStatus[] {
  const where = worklistStatus(key).where(new Date())
  return where.status ? [...where.status.in] : []
}

/**
 * Which tab a job belongs in, and which status inside it.
 *
 * `now` is a parameter rather than a call to `new Date()` so the time-dependent
 * boundary between Scheduled and Follow-up is testable, and so a page renders
 * every row against one instant instead of drifting mid-list.
 */
export function deriveWorklistStatus(job: WorklistInput, now: Date = new Date()): WorklistStatusKey {
  const tab = tabOf(job.status)
  const candidates = WORKLIST_STATUSES.filter((entry) => entry.tab === tab)

  for (const entry of candidates) {
    if (entry.matches(job, now)) return entry.key
  }

  // Unreachable while the tab mapping is total and each tab's predicates are
  // exhaustive, both of which are asserted by tests. Falling back to the tab's
  // first status keeps a row visible rather than dropping it.
  return candidates[0]?.key ?? 'DRAFT'
}

/**
 * The `where` fragment for one tab, optionally narrowed to one status.
 *
 * Without a status this is just the tab's `JobStatus` set, which is what the
 * unfiltered tab shows. With one, it is the derived status expressed as
 * indexable column predicates — the reason the roll-up columns exist.
 */
export function worklistWhere(
  tab: JobTabKey,
  status: WorklistStatusKey | undefined,
  now: Date = new Date(),
): WorklistWhere {
  if (!status) {
    return { status: { in: [...(JOB_TABS.find((entry) => entry.key === tab)?.statuses ?? [])] } }
  }

  return worklistStatus(status).where(now)
}

export interface WorklistGroup<T> {
  key: WorklistStatusKey
  items: T[]
}

/**
 * Split a page of jobs into its derived statuses, in the order the tab presents
 * them, dropping the statuses with nothing in them.
 *
 * Client-side, over rows already fetched, like the generic grouping it replaced.
 * The difference is that a header's *count* no longer comes from here — it comes
 * from `jobs.worklistCounts` and describes every job, not this page. See
 * decision 33.
 */
export function groupByWorklist<T extends WorklistInput>(
  items: readonly T[],
  tab: JobTabKey,
  now: Date = new Date(),
): Array<WorklistGroup<T>> {
  const buckets = new Map<WorklistStatusKey, T[]>()

  for (const job of items) {
    const key = deriveWorklistStatus(job, now)
    const existing = buckets.get(key)
    if (existing) existing.push(job)
    else buckets.set(key, [job])
  }

  return statusesInTab(tab)
    .filter((key) => buckets.has(key))
    .map((key) => ({ key, items: buckets.get(key)! }))
}
